import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/config';
import { useAuth } from './AuthContext';
import { Address } from '../types/address'; 

interface AddressContextType {
  addresses: Address[];
  loading: boolean;
  addAddress: (address: Address) => Promise<{ success: boolean; error?: string }>;
  updateAddress: (id: string, address: Address) => Promise<{ success: boolean; error?: string }>;
  deleteAddress: (id: string) => Promise<{ success: boolean; error?: string }>;
  setDefaultAddress: (id: string) => Promise<{ success: boolean; error?: string }>;
  getDefaultAddress: () => Address | null;
}

const AddressContext = createContext<AddressContextType | undefined>(undefined);

export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within an AddressProvider');
  }
  return context;
};

interface AddressProviderProps {
  children: ReactNode;
}

export const AddressProvider: React.FC<AddressProviderProps> = ({ children }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchAddresses();
    } else {
      setAddresses([]);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data.addresses || []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const addAddress = async (address: Address) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/addresses`, address, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(prev => [...prev, response.data.address]);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to add address' };
    }
  };

  const updateAddress = async (id: string, address: Address) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/addresses/${id}`, address, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(prev => prev.map(addr => addr._id === id ? response.data.address : addr));
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to update address' };
    }
  };

  const deleteAddress = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/addresses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(prev => prev.filter(addr => addr._id !== id));
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to delete address' };
    }
  };

  const setDefaultAddress = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/addresses/${id}/default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(prev => prev.map(addr => ({
        ...addr,
        isDefault: addr._id === id
      })));
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to set default address' };
    }
  };

  const getDefaultAddress = () => {
    return addresses.find(addr => addr.isDefault) || addresses[0] || null;
  };

  return (
    <AddressContext.Provider value={{
      addresses,
      loading,
      addAddress,
      updateAddress,
      deleteAddress,
      setDefaultAddress,
      getDefaultAddress
    }}>
      {children}
    </AddressContext.Provider>
  );
};